import { Button, Form, Tab, Tabs } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import type { TaskCreateFormType } from '@econtractor/types';
import spinnerTitle from 'components/others/spinnerTitle';
import SpinnerBlock from 'components/others/SpinnerBlock';
import TaskTypeSelect from '../shared/TaskTypeSelect';
import useNewTaskForm from './model/useNewTaskForm';
import NewTaskGeneralTab from './tabs/NewTaskGeneralTab';
import NewTaskFilesTab from './tabs/NewTaskFilesTab';
import SolarIcon from 'components/icons/SolarIcon';

interface NewTaskFormProps {
    handleSubmit: (e?: React.FormEvent<HTMLFormElement>) => void;
    isSubmitting: boolean;
    setFieldValue: (field: string, value: unknown) => void;
    values: TaskCreateFormType;
    pendingCloudFiles: File[];
    setPendingCloudFiles: React.Dispatch<React.SetStateAction<File[]>>;
    cloudProgress: number | boolean;
    taskForLocked?: boolean;
}

const NewTaskForm = ({
    handleSubmit,
    isSubmitting,
    setFieldValue,
    values,
    pendingCloudFiles,
    setPendingCloudFiles,
    cloudProgress,
    taskForLocked = false,
}: NewTaskFormProps) => {
    const { t } = useTranslation(['Tasks', 'common']);
    const {
        showFields,
        showDescription,
        setShowDescription,
        activeTab,
        setActiveTab,
        canAssignMyself,
        assignMyself,
        handleEndDateChange,
        handleStartDateChange,
        handleWithTimeChange,
        handleStartTimeChange,
        handleEndTimeChange,
        handleMultiDayChange,
    } = useNewTaskForm({ values, setFieldValue });

    return (
        <Form className="d-flex flex-column h-100" onSubmit={handleSubmit}>
            <div className="off-new-task-form d-flex flex-column h-100">
                <div className="off-new-task-content px-3 position-relative">
                    <SpinnerBlock show={isSubmitting} onlyBlur={true} />

                    <TaskTypeSelect
                        value={values.taskTypeId}
                        onChange={(opt) => setFieldValue('taskTypeId', opt?.value ?? null)}
                        disabled={isSubmitting}
                    />

                    {showFields && (
                        <Tabs
                            variant="underline"
                            activeKey={activeTab}
                            onSelect={(tab) => tab && setActiveTab(tab)}
                            className="mb-3 invoice-tab border-bottom"
                        >
                            <Tab
                                eventKey="general"
                                title={
                                    <>
                                        <SolarIcon name="gear" className="me-2 fs-14" />
                                        {t('tabGeneral')}
                                    </>
                                }
                            >
                                <NewTaskGeneralTab
                                    values={values}
                                    setFieldValue={setFieldValue}
                                    isSubmitting={isSubmitting}
                                    showDescription={showDescription}
                                    setShowDescription={setShowDescription}
                                    canAssignMyself={canAssignMyself}
                                    assignMyself={assignMyself}
                                    handleEndDateChange={handleEndDateChange}
                                    handleStartDateChange={handleStartDateChange}
                                    handleWithTimeChange={handleWithTimeChange}
                                    handleStartTimeChange={handleStartTimeChange}
                                    handleEndTimeChange={handleEndTimeChange}
                                    handleMultiDayChange={handleMultiDayChange}
                                    taskForLocked={taskForLocked}
                                />
                            </Tab>

                            <Tab
                                eventKey="files"
                                title={
                                    <>
                                        <SolarIcon name="paperclip" className="me-2 fs-14" />
                                        {t('tabFiles')}
                                        {pendingCloudFiles.length > 0
                                            ? ` (${pendingCloudFiles.length})`
                                            : ''}
                                    </>
                                }
                            >
                                <NewTaskFilesTab
                                    isSubmitting={isSubmitting}
                                    cloudProgress={cloudProgress}
                                    pendingCloudFiles={pendingCloudFiles}
                                    setPendingCloudFiles={setPendingCloudFiles}
                                />
                            </Tab>
                        </Tabs>
                    )}
                </div>

                <div className="p-3 offcanvas-footer off-new-task-footer">
                    <div className="d-flex justify-content-end">
                        <Button
                            type="submit"
                            variant="info"
                            disabled={isSubmitting || !showFields}
                            className="min100px"
                        >
                            {spinnerTitle(
                                <>
                                    <SolarIcon name="check2-all" className="fs-14 me-2" />
                                    {t('common:buttons.create')}
                                </>,
                                isSubmitting,
                                t('common:buttons.creating'),
                            )}
                        </Button>
                    </div>
                </div>
            </div>
        </Form>
    );
};

export default NewTaskForm;
